import { Patient } from 'api/models';
import { SupportedIdentifiers } from 'types/types';
import { getDateString } from 'utils';
import * as yup from 'yup';

export const getPatientValidationSchema = (
  t: (key: string) => string,
  requiredPatientFields?: string[],
) => {
  const extraValidations = requiredPatientFields
    ? requiredPatientFields.reduce((acc, curr) => {
        if (curr === 'isDead' || curr === 'deathDate') {
          return acc;
        } else {
          acc[curr] = yup.string().required(t('general.errors.required'));
        }

        return acc;
      }, {} as { [key: string]: yup.StringSchema })
    : {};

  const defaultValidations = {
    email: yup.string().email(t('login.errors.email')),
    // custom validator for deathDate
    deathDate: yup
      .string()
      .test(
        'deathDate',
        t('general.errors.required'),
        (value: string | undefined, context) => {
          if (
            requiredPatientFields?.includes('deathDate') &&
            !value &&
            context.parent.isDead === true
          ) {
            return false;
          }

          return true;
        },
      )
      .nullable(),
    patientProjectId: yup.string().required(t('general.errors.required')),
    phoneNumber: yup
      .string()
      // @ts-ignore
      .isPhoneNumber(t('general.errors.hasToBeValidPhoneNumber')),
    identifiers: yup.array().of(
      yup.object({
        id: yup.string().when('type', {
          is: (type: string) => type === SupportedIdentifiers.taj,
          then: () =>
            yup
              .string()
              // @ts-ignore
              .isTajNumber(t('general.errors.hasToBeValidTAJ')),
        }),
        type: yup.string(),
      }),
    ),
  };

  return yup.object({
    ...defaultValidations,
    ...extraValidations,
  });
};

// searchParam type
export const getPatientInitialValues = (
  patient: Patient | undefined,
  searchParams: URLSearchParams,
  patientProjectId?: string,
): Patient => {
  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    birthDate: undefined,
    deathDate: undefined,
    gender: null,
    patientProjectId: patientProjectId ?? '',
    identifiers: [
      {
        type: (searchParams.get('idType') as SupportedIdentifiers) ?? '',
        id: searchParams.get('idNumber') ?? '',
      },
    ],
    titles: '',
    birthFirstName: '',
    birthLastName: '',
    motherFirstName: '',
    motherLastName: '',
    addressPostcode: '',
    addressCity: '',
    addressStreet: '',
    addressHouseNumber: '',
    phoneNumber: '',
    notes: '',
    id: '',
    isDead: false,
  };

  return {
    ...initialValues,
    ...patient,
    ...{
      birthDate: getDateString(patient?.birthDate).replaceAll('.', '-'),
      deathDate: getDateString(patient?.deathDate).replaceAll('.', '-'),
    },
  } as Patient;
};
